import {
  LayoutDashboard, Users, NotebookText, CreditCard, Wallet, Landmark, HandCoins, ScrollText, Building2,
  CalendarDays, BarChart3, Mail, Settings, UserCog, History, PiggyBank, HeartHandshake, Banknote, ListTodo, PartyPopper, Sun, Vault, Receipt, type LucideIcon,
} from 'lucide-react';
import { PAGE_LABELS_HE, type PageKey } from '@shared/permissions.ts';
import type { Icon3DName } from '@/components/Icon3D';

export interface NavItem {
  page: PageKey;
  path: string;
  label: string;
  icon: LucideIcon;
  icon3d: Icon3DName;
  /** Milestone in which the page is built (for the "coming soon" screen). */
  milestone: string;
  group: 'main' | 'finance' | 'shul' | 'admin';
  /** Shown to every reader of financial data (nedarim / payments / ledger / boxes), not by one page key. */
  finance?: boolean;
  /** Shown only to the users with the treasurer's box flag (claim t, owner 2026-10-17). */
  treasurer?: boolean;
}

const item = (page: PageKey, path: string, icon: LucideIcon, icon3d: Icon3DName, milestone: string, group: NavItem['group'], label?: string): NavItem =>
  ({ page, path, label: label ?? PAGE_LABELS_HE[page], icon, icon3d, milestone, group });

/** Menu order. Each item is shown only when the user can read its page (see navVisible). */
export const NAV: NavItem[] = [
  item('dashboard', '/', LayoutDashboard, 'dashboard', 'M6', 'main'),
  item('people', '/people', Users, 'people', 'M1', 'main'),
  item('dashboard', '/tasks', ListTodo, 'calendar', 'M8', 'main', 'משימות'),
  item('services', '/services', ScrollText, 'torah', 'M3', 'shul'),
  item('calendar', '/calendar', CalendarDays, 'calendar', 'M3', 'shul'),
  item('minyanim', '/minyanim', Building2, 'minyanim', 'M3', 'shul'),
  // events: what was ordered for each event (information only) — own permission; boxes users keep access (IMPLIED_BY)
  item('events', '/events', PartyPopper, 'calendar', 'M8', 'shul'),
  item('ledger', '/ledger', NotebookText, 'ledger', 'M1', 'finance'),
  item('payments', '/payments', CreditCard, 'payments', 'M4', 'finance'),
  item('boxes', '/boxes', Wallet, 'cashbox', 'M1', 'finance'),
  // wallets, supports and loans: money of the association → the boxes permission (owner decision 2026-09-27)
  item('boxes', '/wallets', PiggyBank, 'cashbox', 'M8', 'finance', 'ארנקים'),
  item('boxes', '/supports', HeartHandshake, 'expenses', 'M8', 'finance', 'תמיכות'),
  item('boxes', '/loans', Banknote, 'bank', 'M8', 'finance', 'הלוואות'),
  // the invoices folder (owner 2026-11-01): readers of the boxes
  item('boxes', '/invoices', Receipt, 'expenses', 'M8', 'finance', 'חשבוניות'),
  // the treasurer's own box (owner 2026-10-17): only the users marked on their card — never by a page key
  { ...item('boxes', '/treasurer-box', Vault, 'cashbox', 'M8', 'finance', 'קופת גזבר'), treasurer: true },
  item('nedarim', '/nedarim', HandCoins, 'nedarim', 'M2', 'finance'),
  item('bank', '/bank', Landmark, 'bank', 'M5', 'finance'),
  item('reports', '/reports', BarChart3, 'reports', 'M6', 'finance'),
  // the solar roof campaign (owner 2026-10-07): financial readers (canSeeAlerts), no new page key
  { ...item('payments', '/solar', Sun, 'hero-building', 'M9', 'finance', 'קמפיין סולארי'), finance: true },
  item('mailing', '/mailing', Mail, 'mailing', 'M4', 'main'),
  item('users', '/users', UserCog, 'users', 'M0', 'admin'),
  item('audit', '/audit', History, 'audit', 'M0', 'admin'),
  item('settings', '/settings', Settings, 'settings', 'M1', 'admin'),
];

/** Whether a menu item is shown: treasurer items by the flag, finance items to financial readers, others by page. */
export function navVisible(n: NavItem, p: { canRead: (page: PageKey) => boolean; seeAlerts: boolean; treasurer: boolean }): boolean {
  if (n.treasurer) return p.treasurer;
  if (n.finance) return p.seeAlerts;
  return p.canRead(n.page);
}

export const NAV_GROUP_LABELS: Record<NavItem['group'], string | null> = {
  main: null,
  shul: 'בית הכנסת',
  finance: 'כספים',
  admin: 'ניהול',
};
